// Copyright (c) 2026 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import React, { useCallback, useState } from 'react';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { CredentialOffer } from '@daml.js/utility-credential-app-v0/lib/Utility/Credential/App/V0/Model/Offer';
import { AsyncButton } from '../../components/Button/AsyncButton';
import { SyncButton } from '../../components/Button/SyncButton';
import Loading from '../../components/Loading';
import PartyDisplay from '../../components/PartyDisplay';
import { DataTable } from '../../components/Table/DataTable';
import { useParties } from '../../contexts/PartiesContext';
import { useScanProxyApiClient } from '../../contexts/ScanProxyApiContext';
import { useAcceptFreeCredentialOffer } from '../../hooks/mutations/credential/offer/useAcceptFreeCredentialOffer';
import { useAcceptPaidCredentialOffer } from '../../hooks/mutations/credential/offer/useAcceptPaidCredentialOffer';
import { useCancelCredentialOffer } from '../../hooks/mutations/credential/offer/useCancelCredentialOffer';
import useCoins from '../../hooks/queries/cn/useCoin';
import useCredentialOffers from '../../hooks/queries/credential/offer/useCredentialOffers';
import { useCredentialUserService } from '../../hooks/queries/credential/onboarding/useUserService';
import Claims from '../../modules/Credential/Claims';
import TemplateContract from '../../utils/TemplateContract';
import { appTransferContextWithDisclosures } from '../../utils/appTransferContextWithDisclosures';
import { fmt } from '../../utils/common';
import { ERR_ACC_CRED_FREE_OFF } from '../../utils/strings';
import { PageWrapper } from '../PageWrapper';
import { RejectOfferPaidCredentialDialog } from './RejectCredentialOfferDialog';

type Row = TemplateContract<CredentialOffer>;

type Action = {
  name: string;
  action: (r: Row) => Promise<unknown>;
};

const createHeaders: (
  createActions: (r: Row) => Action[],
  setOpenRejectCredentialOfferDialog: React.Dispatch<React.SetStateAction<boolean>>,
  party: string,
  setRow: React.Dispatch<React.SetStateAction<Row | undefined>>,
) => GridColDef<Row>[] = (createActions, setOpenRejectCredentialOfferDialog, party, setRow) => {
  const renderActionCell = (params: GridRenderCellParams<Row>) => {
    const actions = createActions(params.row);
    return (
      <>
        {actions.map(({ name, action }) => (
          <AsyncButton key={name} sx={{ mr: 1 }} onClick={() => action(params.row)}>
            {name}
          </AsyncButton>
        ))}
      </>
    );
  };

  return [
    {
      field: 'operator',
      headerName: 'Operator',
      renderCell: (params) => <PartyDisplay party={params.row.payload.operator} />,
      flex: 100,
      filterable: true,
      valueGetter: (params) => params.row.payload.operator,
    },
    {
      field: 'issuer',
      headerName: 'Issuer',
      renderCell: (params) => <PartyDisplay party={params.row.payload.issuer} />,
      flex: 100,
      filterable: true,
      valueGetter: (params) => params.row.payload.issuer,
    },
    {
      field: 'holder',
      headerName: 'Holder',
      renderCell: (params) => <PartyDisplay party={params.row.payload.holder} />,
      flex: 100,
      filterable: true,
      valueGetter: (params) => params.row.payload.holder,
    },
    {
      field: 'description',
      headerName: 'Description',
      valueGetter: (params) => params.row.payload.description,
      flex: 150,
      filterable: true,
    },
    {
      field: 'feeperdayusd',
      headerName: 'Fee/Day($)',
      valueGetter: (params) => Number(params.row.payload.billingParams?.feePerDayUsd.rate || 0),
      renderCell: (params) => fmt(params.row.payload.billingParams?.feePerDayUsd.rate || 0, 2),
      flex: 150,
      filterable: true,
    },
    {
      field: 'billingperiod',
      headerName: 'Billing Period(mins)',
      valueGetter: (params) => Number(params.row.payload.billingParams?.billingPeriodMinutes || 0),
      flex: 220,
      filterable: true,
    },
    {
      field: 'credentialfeesreserve',
      headerName: 'Fees Reserve($)',
      valueGetter: (params) => Number(params.row.payload.depositInitialAmountUsd || 0),
      renderCell: (params) => fmt(params.row.payload.depositInitialAmountUsd || 0, 2),
      flex: 150,

      filterable: true,
    },
    {
      field: 'claims',
      headerName: 'Claims',
      renderCell: (params) => <Claims claims={params.row.payload.claims} />,
      flex: 250,
      sortable: false,
      filterable: false,
    },
    {
      field: 'actions',
      headerName: 'Accept/Cancel',
      sortable: false,
      renderCell: renderActionCell,
      flex: 250,
      filterable: false,
    },
    {
      field: 'reject',
      headerName: 'Reject',
      sortable: false,
      renderCell: (params) => (
        <SyncButton
          disabled={params.row.payload.holder !== party}
          onClick={() => {
            setOpenRejectCredentialOfferDialog(true);
            setRow(params.row);
          }}
        >
          Reject
        </SyncButton>
      ),
      flex: 150,
      filterable: false,
    },
  ];
};
interface OffersProps {
  disableVirtualization?: boolean;
}
export const Offers = ({ disableVirtualization }: OffersProps) => {
  const [openRejectCredentialOfferDialog, setOpenRejectCredentialOfferDialog] =
    useState<boolean>(false);
  const [getRow, setRow] = useState<Row>();
  const { party } = useParties();
  const credentialOffers = useCredentialOffers();
  const userService = useCredentialUserService();
  const acceptPaid = useAcceptPaidCredentialOffer();
  const acceptFree = useAcceptFreeCredentialOffer();
  const withdrawCredentialOffer = useCancelCredentialOffer();
  const amulets = useCoins();
  const scanProxyApi = useScanProxyApiClient();

  const accept = useCallback(
    async (r: Row) => {
      if (!userService.data) {
        throw new Error(ERR_ACC_CRED_FREE_OFF, { cause: 'User service is not available.' });
      }
      const isPaid = r.payload.billingParams;

      if (isPaid) {
        const { appTransferContext, disclosedContracts } = await appTransferContextWithDisclosures(
          scanProxyApi,
          30,
        );
        const paidPayload = {
          cid: userService.data.contractId,
          argument: {
            credentialOfferCid: r.contractId,
            depositAmulets: (amulets.data ?? []).map((a) => a.contractId),
            appTransferContext,
          },
          meta: {
            disclosedContracts,
          },
        };

        await acceptPaid(paidPayload);
      } else {
        const freePayload = {
          cid: userService.data.contractId,
          argument: {
            credentialOfferCid: r.contractId,
          },
        };
        await acceptFree(freePayload);
      }
    },
    [userService.data, amulets.data, scanProxyApi, acceptPaid, acceptFree],
  );

  const cancel = useCallback(
    async (r: Row) => {
      if (!userService.data) {
        throw new Error(ERR_ACC_CRED_FREE_OFF, { cause: 'User service is not available.' });
      }
      const payload = {
        cid: userService.data.contractId,
        argument: {
          credentialOfferCid: r.contractId,
        },
      };
      await withdrawCredentialOffer(payload);
    },
    [userService.data, withdrawCredentialOffer],
  );

  const isLoading = credentialOffers.isLoading || userService.isLoading;
  if (isLoading) return <Loading />;

  const createActions = (r: Row) => {
    let actions: Action[] = [];
    if (r.payload.holder === party) actions = actions.concat([{ name: 'Accept', action: accept }]);
    if (r.payload.issuer === party) actions = actions.concat([{ name: 'Cancel', action: cancel }]);
    return actions;
  };

  return (
    <PageWrapper pageName="Credential Offers">
      <RejectOfferPaidCredentialDialog
        open={openRejectCredentialOfferDialog}
        onClose={() => setOpenRejectCredentialOfferDialog(false)}
        row={getRow}
      />
      <DataTable
        disableVirtualization={disableVirtualization}
        columns={createHeaders(createActions, setOpenRejectCredentialOfferDialog, party, setRow)}
        rows={credentialOffers.data || []}
        rowKey={(a) => a.contractId}
      />
    </PageWrapper>
  );
};
